import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { GetSubscriptionReports } from "../../../Api/subscription";
import { formatDate } from "../../../Utils/CommanFunctions";
import Tooltip from "@mui/material/Tooltip";

const SubscriptionHistoryTab = ({ filters, page = 0, rowsPerPage = 10, onLoaded }) => {
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const apiParams = useMemo(() => {
        const params = {
            page: page + 1,
            limit: rowsPerPage,
        };
        if (filters?.search) params.search = filters.search;
        if (filters?.startDate) params.startDate = filters.startDate;
        if (filters?.endDate) params.endDate = filters.endDate;
        if (filters?.type) params.type = filters.type;
        if (filters?.planType && filters.planType !== "all") params.planType = filters.planType;
        if (filters?.paymentMethod && filters.paymentMethod !== "all") params.paymentMethod = filters.paymentMethod;
        if (filters?.userId) params.userId = filters.userId;
        return params;
    }, [filters, page, rowsPerPage]);

    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            setIsLoading(true);
            setError("");
            try {
                const res = await GetSubscriptionReports(apiParams);
                const list = res?.data?.data || [];
                const total = res?.data?.totalRecords || 0;
                const totals = res?.data?.totals || null;
                if (!isMounted) return;
                setRows(list);
                onLoaded && onLoaded({ total, totals, page: res?.data?.page });
            } catch (err) {
                if (!isMounted) return;
                setError("Failed to load reports");
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };
        fetchData();
        return () => {
            isMounted = false;
        };
    }, [apiParams, onLoaded]);



    return (
        <div className="cards-table card-table">
            <div className="card-body">
                <div className="table-responsive custom-pagination">
                    <table className="table datatable table-manager mb-0 table-no-padding" style={{ width: '100%', tableLayout: 'fixed' }}>
                        <thead className="thead-light">
                            <tr>
                                <th className="text-start" style={{ width: '20%' }}>Name</th>
                                <th className="text-start" style={{ width: '25%' }}>Email</th>
                                <th className="text-start" style={{ width: '15%' }}>Plan Type</th>
                                <th className="text-start" style={{ width: '10%' }}>Amount</th>
                                <th className="text-start" style={{ width: '10%' }}>Method</th>
                                <th className="text-start" style={{ width: '10%' }}>Start Date</th>
                                <th className="text-start" style={{ width: '10%' }}>End Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && (
                                <tr>
                                    <td align="center" colSpan={7}>
                                        <div className="d-flex justify-content-center align-items-center py-4">
                                            <div className="spinner-border loader-theme" role="status" />
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {!isLoading && error && (
                                <tr>
                                    <td className="text-center" colSpan={7}>{error}</td>
                                </tr>
                            )}
                            {!isLoading && !error && rows?.length === 0 && (
                                <tr>
                                    <td className="text-center" colSpan={7}>No records found</td>
                                </tr>
                            )}
                            {!isLoading && !error && rows?.map((item) => (
                                <tr key={item?._id}>
                                    <td>
                                        <Tooltip title={item?.name || '-'} placement="top" arrow>
                                            <span className="user-cell-ellipsis">
                                                {item?._id ? (
                                                    <Link className="subscription-neutral-link" to="/admin/client-user-detail" state={{ id: item?.userId }}>
                                                        {item?.name || '-'}
                                                    </Link>
                                                ) : (item?.name || '-')}
                                            </span>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <Tooltip title={item?.email || '-'} placement="top" arrow>
                                            <span className="user-cell-ellipsis">
                                                {item?.email || '-'}
                                            </span>
                                        </Tooltip>
                                    </td>
                                    <td className="text-capitalize">{item?.isFreeTrial ? `Free trial (${item?.planType})` : (item?.planType || '-')}</td>
                                    <td>£{item?.isFreeTrial ? 0 : (item?.amount != null ? (Number(item?.amount) % 1 === 0 ? Number(item?.amount) : Number(item?.amount).toFixed(2)) : '-')}</td>
                                    <td className="text-capitalize">
                                        {item?.isCashAmount ? "Manual (cash)" :
                                            item?.intentType === "stripe" ? "Stripe" :
                                                item?.intentType === "apple_pay" ? "Apple Pay" :
                                                    item?.intentType === "google_play" ? "Google Play" :
                                                        "Stripe"}
                                    </td>
                                    <td>{formatDate(item?.startDate)}</td>
                                    <td>{formatDate(item?.endDate)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionHistoryTab;
